import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic } from "trader.ts/ui";
import { Form, dateBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import {
  enrollment_status,
  enrollmentid,
  formula_type,
  formulaid,
  instrumentid,
  internalteacherid,
  lesson_location,
  lesson_removed_reason,
  lesson_status,
  lessongroupid,
  lessonid,
  periodid,
  planningmode,
  roomid,
  siteid,
  studentid,
  teacherid,
  weekday,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import {
  calendar_slot,
  renderWeekCalendar,
} from "../../client/renderweekcalendar";
import {
  goForwardXDaysInsidePeriods as getNextDateInsidePeriods,
  goBackXDaysInsidePeriods as getPreviousDateInsidePeriods,
  period,
} from "../../domain/periods";
import { capitalize } from "../../lib/capitalize";
import {
  bluePlanningColors,
  greyPlanningColors,
  makeGradient,
  purple,
} from "../../lib/colors";
import { isEqual } from "../../lib/isequal";
import { isStudentActiveAtLesson } from "../../lib/isstudentactiveatlesson";
import { range } from "../../lib/range";
import {
  allWeekdays,
  dateInDaterange,
  getFirstWeekdayFrom,
  getMonday,
  timerangesOverlap,
} from "../../lib/time";
import {
  lessonStatus,
  parseLessonRemovedReason,
  parseLessonStatus,
} from "../../parsing_from_ur";
import {
  HHmm,
  ddMMM,
  ddMMMM,
  renderFirstNameLastName,
  renderLessonLocation,
} from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import {
  filenote,
  renderExistingNote,
  renderPerLesson,
  textnote,
} from "../notes/noteswidget";
import {
  calcSelectedElementDimensionsForCalendarSlot,
  makeAndTrackSmallLayoutS,
  renderFloatingPopup,
} from "../planning/floatingpopup";
import {
  maybeRenderFlexCount,
  renderClosePopupButton,
} from "../planning/selectionandforms";
import { planningDateKind } from "../planning_start_public/client";

type lessonSlot = {
  lessongroup: {
    id: lessongroupid;
    time: joda.LocalTime;
    type: formula_type;
    weekday: weekday;
    lessonduration: number;
    numberoflessons: number;
    planningMode: planningmode;
    location: lesson_location;
    internalTeacherId: internalteacherid;
    roomId: roomid | null;
    description: string | null;
    firstLesson: joda.LocalDate | null;
    lastLesson: joda.LocalDate | null;
    startsinperiod: periodid;
  };
  lesson: {
    id: lessonid;
    time: joda.LocalTime;
    date: joda.LocalDate;
    removedreason: lesson_removed_reason | null;
  };
  formula: { id: formulaid; numberoflessons: number };
  teacherplanned: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  teacheractual: {
    id: teacherid;
    internalteacherid: internalteacherid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    id: instrumentid;
    description: string;
  };
  room: {
    description: string | null;
    siteid: siteid | null;
  };
  students:
    | {
        enrollmentid: enrollmentid;
        id: studentid;
        firstname: string;
        lastname: string;
        enrollment_startedlessonson: joda.LocalDate;
        enrollment_stoppedlessonson: joda.LocalDate | null;
        enrollment_status: enrollment_status;
      }[]
    | null;
  replacement_lesson: { id: lessonid | null; date: joda.LocalDate | null };
  filenotes_previous_lessons: filenote[];
  textnotes_previous_lessons: textnote[];
  notes: Source<{
    filenotes: filenote[];
    textnotes: textnote[];
  }>;
  presences: Source<
    {
      studentid: studentid;
      status: lessonStatus;
    }[]
  >;
};

type mode =
  | { tag: "day"; date: joda.LocalDate; selected: null | lessonid }
  | { tag: "week"; selected: null | lessonid };
const modeC = routes.magicCodec<mode>();
const modeRoute = makeRoute("/mode/{mode:mode}", {
  mode: makeEncoderFromCodec(modeC),
});

export function render(opts: {
  schoolname: string;
  lessons_: (Omit<lessonSlot, "notes" | "presences"> & {
    filenotes: filenote[] | null;
    textnotes: textnote[] | null;
    presences:
      | {
          studentid: studentid;
          status: lesson_status;
        }[]
      | null;
  })[];
  teacher: { firstname: string; lastname: string; id: teacherid };
  period: period;
  nextPeriod: period | null;
  previousPeriod: period | null;
  days: { date: joda.LocalDate; kind: planningDateKind }[];
  date: joda.LocalDate;
  mode: undefined | "day" | "week";
}): HTMLElement | HTMLElement[] {
  const router = makeClientsideRouter(
    modeRoute,
    opts.mode === undefined || opts.mode === "day"
      ? {
          tag: "day",
          date: opts.date,
          selected: null,
        }
      : { tag: "week", selected: null },
  );
  const modeS: Source<mode> = router.start();

  if (opts.lessons_.length === 0) {
    // Can't happen, stopped on server
    return <div></div>;
  }
  const lessons: lessonSlot[] = opts.lessons_.map((l) => ({
    ...l,
    notes: new Source({
      textnotes: l.textnotes || [],
      filenotes: l.filenotes || [],
    }),
    presences: new Source(
      (l.presences || []).map((p) => ({
        ...p,
        status: parseLessonStatus(p.status),
      })),
    ),
  }));
  const startHour = lessons
    .minBy((l) => l.lesson.time.toSecondOfDay())!
    .lesson.time.hour();
  const lastLesson = lessons.maxBy((l) =>
    l.lesson.time.plusMinutes(l.lessongroup.lessonduration).toSecondOfDay(),
  )!;
  const endTime = lastLesson.lesson.time.plusMinutes(
    lastLesson.lessongroup.lessonduration,
  );
  const endHour = endTime.minute() > 0 ? endTime.hour() + 1 : endTime.hour();

  const smallLayoutS = makeAndTrackSmallLayoutS({ maxWidth: 768 });

  const today = joda.LocalDate.now();

  const popup = renderFloatingPopup(
    modeS,
    new Source(lessons),
    smallLayoutS,
    function (
      mode: mode,
      slots: lessonSlot[],
    ): { mode: mode; slot: lessonSlot } | null {
      if (mode.selected !== null) {
        const slot = slots.find((l) => l.lesson.id === mode.selected);
        if (!slot) {
          return null;
        } else {
          return { mode, slot: slot };
        }
      } else {
        return null;
      }
    },
    {
      getSelectedElementDimensions: function ({ slot }) {
        return calcSelectedElementDimensionsForCalendarSlot(startHour, {
          time: slot.lesson.time,
          weekday: slot.lesson.date.dayOfWeek(),
          duration: slot.lessongroup.lessonduration,
        });
      },
      calcPopupWidth: () => 480,
      getApproximateFloatingPopupHeight: () => 600,
      renderPopupContents: function ({ mode, slot }) {
        return renderLessonDetailPopup(
          opts.schoolname,
          opts.teacher,
          mode.tag === "week",
          slot,
          () =>
            (document.location.hash = router.makeHashLink({
              ...mode,
              selected: null,
            })),
        );
      },
      modeIsDayView: (m: mode) => m.tag === "day",
      offsetRight: 8,
      offsetLeft: 8 + 35, // time indicators
    },
  );

  return dynVariadic([modeS, smallLayoutS], ([mode, smallLayout]) => {
    const renderedSlots = mapPartial(lessons, (l): calendar_slot | null => {
      const rr =
        l.lesson.removedreason &&
        parseLessonRemovedReason(l.lesson.removedreason);
      if (
        rr === "DeletedManually" ||
        rr === "RemovedBecauseOfNewHoliday" ||
        rr === "RemovedBecauseOfRemovalOfHoliday"
      ) {
        return null;
      }
      const slotType = calcSlotType(opts.teacher, l);
      if (
        slotType === "removed_or_substituted" &&
        lessons.some(
          (otherl) =>
            otherl.lesson.id !== l.lesson.id &&
            timerangesOverlap(
              {
                time: otherl.lesson.time,
                duration: otherl.lessongroup.lessonduration,
                weekday: otherl.lesson.date.dayOfWeek(),
              },
              {
                time: l.lesson.time,
                duration: l.lessongroup.lessonduration,
                weekday: l.lesson.date.dayOfWeek(),
              },
            ),
        )
      ) {
        return null;
      }
      const selectedClass =
        l.lesson.id === mode.selected ? planningstyle.selected : "";
      const notSelectedClass =
        mode.selected === null
          ? ""
          : l.lesson.id !== mode.selected
          ? style.seethrough
          : "";
      const absentClass =
        slotType === "removed_or_substituted"
          ? planningstyle.slot_absence + style.seethrough
          : "";

      const lastLessonIndicator = isEqual(
        l.lesson.date,
        l.lessongroup.lastLesson,
      ) ? (
        <div style="font-size: 24px; height: 10px; margin-left: 4px; display: inline-block;">
          *
        </div>
      ) : (
        []
      );

      return {
        time: l.lesson.time,
        duration: l.lessongroup.lessonduration,
        weekday: l.lesson.date.dayOfWeek(),
        contents: (
          <div>
            <div
              className={planningstyle.indicators}
              style="border: 0px; margin-right: 6px; background: none;"
            >
              {/* asterisk */ lastLessonIndicator}

              {
                /* purple circle */
                l.lesson.removedreason &&
                parseLessonRemovedReason(l.lesson.removedreason) ===
                  "StudentLegalAbsence" &&
                l.replacement_lesson.date !== null ? (
                  <div
                    className={planningstyle.lesson_status}
                    style={`background-color: ${purple.primary}`}
                  ></div>
                ) : (
                  []
                )
              }

              {
                /* lesson status circle */
                slotType === "normal"
                  ? dyn(l.presences, (presences) =>
                      renderLessonStatusCircle(
                        l.lesson.date,
                        l.lessongroup.type,
                        l.students || [],
                        presences,
                      ),
                    )
                  : []
              }
            </div>
            {(l.students || []).length === 1
              ? renderFirstNameLastName(l.students![0])
              : (l.students || [])
                  .filter((s) => isStudentActiveAtLesson(l.lesson.date, s))
                  .map((s) => s.firstname)
                  .orderBy((s) => s, "asc")
                  .join(", ")}
          </div>
        ),
        colors:
          slotType === "normal"
            ? bluePlanningColors
            : slotType === "removed_or_substituted"
            ? greyPlanningColors
            : checkAllCasesHandled(slotType),
        onclick: {
          href: router.makeHashLink({ ...mode, selected: l.lesson.id }),
        },
        className: selectedClass + notSelectedClass + absentClass,
        internalteacherid: 1 as internalteacherid,
      };
    });
    const calendar = renderWeekCalendar({
      hours: [startHour, endHour],
      weekdays: (mode.tag === "week"
        ? opts.days
        : mode.tag === "day"
        ? opts.days.filter((d) => d.date.isEqual(mode.date))
        : checkAllCasesHandled(mode)
      ).map((d) => [
        d.date.dayOfWeek(),
        {
          label: smallLayout ? "" : d.date.dayOfMonth().toString(),
          sublabel: smallLayout
            ? ""
            : translateWeekday(d.date.dayOfWeek()).slice(0, 2) +
              (mode.tag === "day" ? " ︎" : " ↓"),
          link:
            mode.tag === "week"
              ? router.makeHashLink({
                  tag: "day",
                  date: d.date,
                  selected: mode.selected,
                })
              : undefined,
          contents:
            d.kind.tag === "holiday"
              ? { tag: "holiday", description: d.kind.description }
              : d.kind.tag === "outsideperiod"
              ? { tag: "past" }
              : d.kind.tag === "open"
              ? d.date.isBefore(today)
                ? { tag: "past" }
                : d.date.isEqual(today)
                ? { tag: "today" }
                : null
              : checkAllCasesHandled(d.kind),
        },
      ]),
      alwaysShowGroups: false,
      groupByTeachers: [
        { internalteacherid: 1 as internalteacherid, firstname: "" },
      ],
      collapseEmptyTeachers: false,
      slots: renderedSlots,
      onClickOutsideSlot: () => {
        document.location.hash = router.makeHashLink({
          ...mode,
          selected: null,
        });
      },
    });

    const preventScrollStyle =
      mode.tag === "week" && mode.selected !== null
        ? "overflow: hidden;" /* don't allow scrolling calendar when popup shows*/
        : "overflow-y: auto;";
    const maxWidthStyle =
      mode.tag === "week"
        ? "max-width: unset;"
        : "max-width: 1080px; margin: 0px auto;";

    const popupBackdrop = (() => {
      if (
        smallLayout === false ||
        mode.selected === null ||
        mode.tag === "week"
      ) {
        return <span></span>;
      }

      const found = lessons.find(
        (l) =>
          l.lesson.id === mode.selected && l.lesson.date.isEqual(mode.date),
      );
      if (!found) {
        return <span></span>;
      }

      return (
        <div
          className={style.popup_backdrop}
          onclick={() =>
            (document.location.hash = router.makeHashLink({
              tag: "day",
              date: mode.date,
              selected: null,
            }))
          }
        ></div>
      );
    })();
    return (
      <div style={maxWidthStyle}>
        {renderNavButtons(
          router,
          mode.tag === "day" ? mode.date : null,
          opts.teacher.id,
          mode,
          {
            period: opts.period,
            nextPeriod: opts.nextPeriod,
            previousPeriod: opts.previousPeriod,
          },
          opts.days,
          lessons,
        )}
        <main
          className={mode.tag === "day" ? style.equal_columns : ""}
          style={`
        max-width: unset;
        ${preventScrollStyle}
    `}
        >
          {calendar}
          <div>{popup}</div>
        </main>
        {popupBackdrop}
      </div>
    );
  });
}

function renderNavButtons(
  router: {
    makeHashLink: (m: mode) => string;
  },
  d: joda.LocalDate | null,
  teacherid: teacherid,
  mode: mode,
  {
    period,
    previousPeriod,
    nextPeriod,
  }: {
    period: period;
    previousPeriod: period | null;
    nextPeriod: period | null;
  },
  days: { date: joda.LocalDate; kind: planningDateKind }[],
  lessons: { lesson: { date: joda.LocalDate } }[],
) {
  const centralDate = d || days.first()!.date;
  const prevDate = getPreviousDateInsidePeriods({
    currentDate:
      mode.tag === "day"
        ? centralDate
        : getFirstWeekdayFrom(centralDate, joda.DayOfWeek.SUNDAY),
    numberOfDays: 7,
    period,
    previousPeriod,
  });
  const nextDate = getNextDateInsidePeriods({
    currentDate: mode.tag === "day" ? centralDate : getMonday(centralDate),
    numberOfDays: 7,
    period,
    nextPeriod,
  });

  const navDateForm = new Form({})
    .addField("date", [], () => dateBox({ initialVal: centralDate }))
    .build();

  navDateForm.s.observe((p) => {
    if (p.tag === "parsed") {
      const d = p.parsed.date;
      const dInDays = dateInDaterange(
        { date: d },
        { from: days.first()!.date, until: days.last()!.date },
      );
      if (mode.tag === "day") {
        if (!dInDays) {
          document.location = routes.teacher_lessonrosterRoute.link({
            date: d,
            mode: "day",
            teacherid,
          });
        } else {
          if (!d.isEqual(mode.date)) {
            document.location.hash = router.makeHashLink({
              tag: "day",
              date: d,
              selected: null,
            });
          }
        }
      } else {
        if (!dInDays) {
          document.location = routes.teacher_lessonrosterRoute.link({
            date: d,
            mode: "week",
            teacherid,
          });
        }
      }
    }
  });

  return (
    <div>
      <div
        className={planningstyle.buttonbar}
        style="max-width: 1080px; margin: 0px auto 0px auto;"
      >
        <div
          className={planningstyle.buttonbar_section + style.remove_on_phone}
          style="flex: 1"
        >
          {mode.tag === "day" ? (
            <a
              className={style.a_around_button}
              href={router.makeHashLink({
                tag: "week",
                selected: mode.selected,
              })}
            >
              <button
                className={style.button + style.button_em_down}
                style="margin-bottom: 0px;"
              >
                <i
                  className={FA.icon_arrow_left}
                  style="transform: rotate(90deg)"
                ></i>
                {"Week"}
              </button>
            </a>
          ) : (
            <span></span>
          )}
        </div>
        <div className={planningstyle.buttonbar_section}>
          <a
            className={style.a_around_button}
            href={
              prevDate === null
                ? ""
                : routes.teacher_lessonrosterRoute.link({
                    date: prevDate,
                    teacherid: teacherid,
                    mode: mode.tag,
                  })
            }
          >
            <button
              disabled={nextDate === null ? true : undefined}
              className={
                style.button + style.button_em_down + style.button_has_only_icon
              }
              style="margin-bottom: 0px;"
            >
              <i className={FA.icon_arrow_left}></i>
            </button>
          </a>

          <label
            className={
              planningstyle.hidden_date_input +
              style.button +
              style.button_em_down
            }
          >
            <div className={planningstyle.weekdates}>
              <div>
                <b>
                  {mode.tag === "week"
                    ? ddMMMM(days.first()!.date) +
                      " - " +
                      ddMMMM(days.last()!.date)
                    : translateWeekday(mode.date.dayOfWeek()).slice(0, 2) +
                      " " +
                      ddMMMM(mode.date)}
                </b>
              </div>
              <div></div>
            </div>
            <span>{navDateForm.render()}</span>
          </label>

          <a
            className={style.a_around_button}
            href={
              nextDate === null
                ? ""
                : routes.teacher_lessonrosterRoute.link({
                    date: nextDate,
                    teacherid: teacherid,
                    mode: mode.tag,
                  })
            }
          >
            <button
              disabled={nextDate === null ? true : undefined}
              className={
                style.button + style.button_em_down + style.button_has_only_icon
              }
              style="margin-bottom: 0px;"
            >
              <i className={FA.icon_arrow_right}></i>
            </button>
          </a>
        </div>
      </div>
      <div
        className={planningstyle.buttonbar + style.only_on_phone}
        style="background: var(--grey-200); color: var(--grey-700);"
      >
        {allWeekdays.map((wd) => {
          const hasLessons = lessons.some((l) =>
            isEqual(l.lesson.date.dayOfWeek(), wd),
          );
          const selectedWeekdayStyle = `
            background-color: var(--grey-000);
            border-color: var(--grey-400);
            border-bottom: 0px;
            border-top: 0px;`;
          return (
            <a
              className={planningstyle.buttonbar_section}
              style={
                "height: 100%; border: 1px solid transparent;" +
                (d && isEqual(d.dayOfWeek(), wd) ? selectedWeekdayStyle : "")
              }
              onclick={() =>
                (location.hash = router.makeHashLink({
                  tag: "day",
                  date: getFirstWeekdayFrom(getMonday(centralDate), wd),
                  selected: null,
                }))
              }
            >
              <button
                style={
                  "font-size: 1.15rem; padding: 0px; height: 100%; position: relative;" +
                  (hasLessons ? "font-weight: bold;" : "")
                }
                className={
                  style.button +
                  style.button_em_down +
                  style.button_has_only_icon
                }
              >
                {capitalize(translateWeekday(wd)).substring(0, 2)}
              </button>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function calcSlotType(teacher: { id: teacherid }, l: lessonSlot) {
  const rr = l.lesson.removedreason
    ? parseLessonRemovedReason(l.lesson.removedreason)
    : null;

  return rr !== null ||
    (l.teacheractual.id !== l.teacherplanned.id &&
      l.teacheractual.id !== teacher.id)
    ? "removed_or_substituted"
    : "normal";
}

function renderLessonDetailPopup(
  schoolname: string,
  teacher: { id: teacherid },
  shownInPopup: boolean,
  l: lessonSlot,
  goToNormal: () => void,
) {
  const slotType = calcSlotType(teacher, l);
  const colors =
    slotType === "normal"
      ? bluePlanningColors
      : slotType === "removed_or_substituted"
      ? greyPlanningColors
      : checkAllCasesHandled(slotType);
  function truncate(str: string, n: number) {
    return str.length > n ? str.slice(0, n - 1) + "..." : str;
  }
  const header = (
    <div
      className={style.panel_header}
      style={`background: ${makeGradient(colors)}; color: ${colors.text}`}
    >
      {renderClosePopupButton(goToNormal)}
      <article className={style.text_with_icon}>
        <i className={FA.icon_user_solid}></i>
        <div>
          <b>
            {truncate(
              (l.students || [])
                .filter((s) => isStudentActiveAtLesson(l.lesson.date, s))
                .map((s) => s.firstname)
                .orderBy((s) => s, "asc")
                .join(", "),
              60,
            )}
          </b>
        </div>
      </article>
      <div style="display: flex">
        <div style="flex: 1" className={style.text_with_icon}>
          <i className={FA.icon_clock_regular}></i>
          <div>
            {ddMMM(l.lesson.date)} - {l.lesson.time.format(HHmm)}
          </div>
        </div>
        <div style="text-align: right" className={style.text_with_icon}>
          <div>
            {renderLessonLocation(
              l.lessongroup.location,
              l.room?.description || null,
            )}
          </div>
          <i className={FA.icon_map_marker_alt_solid + style.icon_on_right}></i>
        </div>
      </div>
    </div>
  );

  const previousNotesRendered = [
    ...l.textnotes_previous_lessons.map(
      (tn): [HTMLElement | HTMLElement[], joda.LocalDateTime] => [
        renderExistingNote(
          schoolname,
          tn,
          {
            smallbuttons: false,
            isPastNote: true,
          },
          { tag: "yes", students: l.students || [] },
        ),
        tn.stamp,
      ],
    ),
    ...l.filenotes_previous_lessons.map(
      (fn): [HTMLElement | HTMLElement[], joda.LocalDateTime] => [
        renderExistingNote(
          schoolname,
          fn,
          {
            smallbuttons: false,
            isPastNote: true,
          },
          { tag: "yes", students: l.students || [] },
        ),
        fn.stamp,
      ],
    ),
  ];
  const previousNotesRenderedAndOrdered = previousNotesRendered
    .orderBy(([_, d]) => d.atZone(joda.ZoneId.UTC).toEpochSecond(), "desc")
    .map(([h, _]) => h);

  const notes = () =>
    dyn(l.notes, (notes) =>
      renderPerLesson(
        schoolname,
        l.lesson.id,
        notes,
        { id: l.lessongroup.id, students: l.students || [] },
        {
          smallbuttons: false,
          fullWidthButtons: true,
          userIsStudent: false,
          reload: async () => {
            const newNotes = await rpc(
              routes.getNotesForLessonSpec,
              undefined,
              {
                lessonid: l.lesson.id,
              },
            );
            if (newNotes !== null) {
              l.notes.set({
                textnotes: newNotes.textnotes || [],
                filenotes: newNotes.filenotes || [],
              });
            }
          },
        },
      ),
    );

  const instrumentSection = (
    <div style="flex: 1; font-size: 1rem;" className={style.text_with_icon}>
      <i className={FA.icon_music_solid}></i>
      <div>{capitalize(l.instrument.description.toLowerCase())}</div>
    </div>
  );

  const linksSection = (
    <div style="">
      <a
        target="_blank"
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: l.lessongroup.id,
        })}
      >
        <span>{"Details lessenreeks"}</span>
      </a>
      {" / "}
      <a
        target="_blank"
        href={routes.lessongroup_lessondataRoute.link({
          selecteddate: l.lesson.date,
          lessongroupid: l.lessongroup.id,
          monthsinfuture: undefined,
        })}
      >
        <span>{"Lesdata"}</span>
      </a>
      <i
        className={FA.icon_link_solid + style.icon_on_right}
        style="opacity: 0.75;"
      ></i>
    </div>
  );

  const lastLessonSection = l.lessongroup.lastLesson?.isEqual(l.lesson.date) ? (
    <article style="margin-left: 4px;">
      <i style="left: 4px;">*</i>{" "}
      <b>
        {"Laatste les"}
        {maybeRenderFlexCount(l.lessongroup, l.formula)}
      </b>
    </article>
  ) : (
    <span></span>
  );

  function renderRemovedLessonDetails(l: lessonSlot): HTMLElement {
    const wrapWithHtml = (s: string) => (
      <div className={style.panel} style="font-weight: bold; margin: -8px;">
        {s}
      </div>
    );
    if (l.teacheractual.id !== l.teacherplanned.id) {
      return wrapWithHtml(
        "Les overgenomen door docent %teacher".replace(
          "%teacher",
          renderFirstNameLastName(l.teacheractual),
        ),
      );
    } else {
      const showRemovedReason = () => {
        if (l.lesson.removedreason === null) {
          return "";
        }
        switch (parseLessonRemovedReason(l.lesson.removedreason)) {
          case "MovedDueToTeacherAbsence":
            return l.replacement_lesson.date !== null
              ? "Inhaalles gepland op %date".replace(
                  "%date",
                  ddMMMM(l.replacement_lesson.date),
                )
              : "Inhaalles gepland";
          case "DeletedDueToTeacherAbsence":
            return "Les afgelast";
          case "DeletedManually":
            return "Les verwijderd";
          case "RemovedBecauseOfNewHoliday":
            return ""; // filtered
          case "RemovedBecauseOfRemovalOfHoliday":
            return ""; // filtered
          case "StudentLegalAbsence":
            return l.replacement_lesson.date !== null
              ? "Student afwezig, vervangles op %date".replace(
                  "%date",
                  ddMMMM(l.replacement_lesson.date),
                )
              : "Student afwezig";
        }
      };
      return wrapWithHtml(showRemovedReason());
    }
  }

  return (
    <div
      className={style.panel}
      style={
        "margin-bottom: 0px;" +
        (shownInPopup
          ? "box-shadow: 0px 2px 8px 4px rgba(0, 0, 0, 0.3); max-height: 99vh; overflow-y: auto; "
          : "")
      }
    >
      {header}

      <div style="display: flex; margin-bottom: 24px; margin-top: -12px; font-size: 0.85em; padding: 0px 8px;">
        {instrumentSection}
        {linksSection}
      </div>

      {lastLessonSection}

      {slotType === "normal" ? (
        <div>
          <label>{"Aanwezig"}</label>
          <section>{renderPresences(l, (opts) => opts)}</section>

          <div style="display: flex;">
            <label style="flex: 1">{"Notities"}</label>
            <a
              style="font-size: 0.85em;"
              target="_blank"
              href={routes.notesRoute.link({
                selected: { lessongroup: l.lessongroup.id },
              })}
            >
              <span>{"Alle"}</span>
              <i
                className={FA.icon_link_solid + style.icon_on_right}
                style="opacity: 0.75; margin-right: 4px;"
              ></i>
            </a>
          </div>
          <article>{notes()}</article>
          {previousNotesRenderedAndOrdered.length > 0 ? (
            <label for="">{"Notities vorige lessen"}</label>
          ) : (
            []
          )}
          {previousNotesRenderedAndOrdered}
        </div>
      ) : (
        renderRemovedLessonDetails(l)
      )}
    </div>
  );
}

export function renderLessonStatusCircle(
  lessondate: joda.LocalDate,
  type: formula_type,
  students: {
    enrollmentid: enrollmentid;
    id: studentid;
    enrollment_startedlessonson: joda.LocalDate;
    enrollment_stoppedlessonson: joda.LocalDate | null;
    enrollment_status: enrollment_status;
  }[],
  presences: {
    studentid: studentid;
    status: lessonStatus;
  }[],
  extraStyle?: string,
): HTMLElement | HTMLElement[] {
  const st =
    makeStatusIndicatorStyle(lessondate, type, students, presences) +
    ";" +
    (extraStyle || "");
  return (
    <div className={planningstyle.lesson_status} style={st}>
      &nbsp;
    </div>
  );
}

export function makeStatusIndicatorStyle(
  lessondate: joda.LocalDate,
  type: formula_type,
  students: {
    enrollmentid: enrollmentid;
    id: studentid;
    enrollment_startedlessonson: joda.LocalDate;
    enrollment_stoppedlessonson: joda.LocalDate | null;
    enrollment_status: enrollment_status;
  }[],
  presences: {
    status: lessonStatus;
  }[],
) {
  const length = students.filter((s) =>
    isStudentActiveAtLesson(lessondate, s),
  ).length;

  const open = range(0, Math.max(0, length - presences.length)).map(() => ({
    status: "Open" as const,
  }));
  const percentagePerPart = 100 / length;
  let i = 0;
  const gradientParts = [...presences, ...open]
    .orderBy((pres) => getOrder(pres.status), "asc")
    .map((pres) => {
      const color = getColor(pres.status);
      const grad =
        color +
        " " +
        (percentagePerPart * i - 1).toString() +
        "%, " +
        color +
        " " +
        (percentagePerPart * (i + 1) - 1).toString() +
        "%";
      i++;
      return grad;
    })
    .join(", ");
  return "background: linear-gradient(90deg," + gradientParts + ")";

  function getOrder(ls: lessonStatus) {
    if (ls === "Open") {
      return 0;
    } else if (ls === "Done") {
      return 1;
    } else if (ls === "StudentAbsent") {
      return 2;
    } else {
      return checkAllCasesHandled(ls);
    }
  }
  function getColor(ls: lessonStatus) {
    if (ls === "Open") {
      return "white";
    } else if (ls === "Done") {
      return "#4d8ef3";
    } else if (ls === "StudentAbsent") {
      return "#f38e8e";
    } else {
      return checkAllCasesHandled(ls);
    }
  }
}

export function renderPresences<
  T extends {
    studentid: studentid;
    status: lessonStatus;
  },
>(
  l: {
    lesson: { id: lessonid; date: joda.LocalDate };
    lessongroup: { type: formula_type };
    students:
      | {
          enrollmentid: enrollmentid;
          id: studentid;
          firstname: string;
          lastname: string;
          enrollment_startedlessonson: joda.LocalDate;
          enrollment_stoppedlessonson: joda.LocalDate | null;
          enrollment_status: enrollment_status;
        }[]
      | null;
    presences: Source<T[]>;
  },
  makeNewPresence: (opts: { studentid: studentid; status: lessonStatus }) => T,
) {
  return dyn(l.presences, (presences): HTMLElement[] => {
    return (l.students || [])
      .filter((s) => isStudentActiveAtLesson(l.lesson.date, s))
      .orderBy(renderFirstNameLastName)
      .map((s) => {
        const existingPresence = presences.find((p) => p.studentid === s.id);
        const currentPresence = existingPresence
          ? existingPresence.status
          : ("Open" as const);
        function setStatus(sta: lessonStatus) {
          // fire and forget
          void rpc(routes.setStudentPresenceSpec, undefined, {
            lessonid: l.lesson.id,
            studentid: s.id,
            status: sta,
            comments: "",
          });
          l.presences.set(
            l.presences
              .get()
              .filter((p) => p.studentid !== s.id)
              .concat([makeNewPresence({ studentid: s.id, status: sta })]),
          );
        }
        return (
          <div style="display: flex; align-items: center; ">
            <div style="flex: 1; background-color: var(--grey-000); border-radius: 8px 0px 0px 8px; padding: 8px 16px;">
              <a
                href={routes.studentOverviewRoute.link({ studentid: s.id })}
                style="text-decoration: none"
              >
                {renderFirstNameLastName(s)}
              </a>
            </div>
            <button
              onclick={() =>
                currentPresence === "StudentAbsent"
                  ? setStatus("Open")
                  : setStatus("StudentAbsent")
              }
              className={
                style.button_has_only_icon +
                (currentPresence === "StudentAbsent"
                  ? style.button_red
                  : style.button_em_down)
              }
              style="border-radius: 0px;;"
            >
              <i
                className={FA.icon_plus_solid}
                style="transform: rotate(45deg); position: relative; top: 2px"
              ></i>
            </button>
            <button
              onclick={() =>
                currentPresence === "Done"
                  ? setStatus("Open")
                  : setStatus("Done")
              }
              className={
                style.button_has_only_icon +
                (currentPresence === "Done"
                  ? style.button_blue_bright
                  : style.button_em_down)
              }
              style="border-radius: 0px 8px 8px 0px; margin-left: 0px; border-left: 1px solid var(--grey-200);"
            >
              <i
                className={FA.icon_check_solid}
                style="position: relative; top: 2px;"
              ></i>
            </button>
          </div>
        );
      });
  });
}
